/**
 * What the health strip says, decided away from the DOM.
 *
 * C12 is the reason there is a strip at all: the pipeline had no clock for
 * eight days and every screen in the panel agreed that campaigns were sending.
 * The lesson was not "add a health check" — it was that **nothing was capable
 * of contradicting the happy path.**
 *
 * So the rule here is that a missing answer is never a green one. Three states,
 * not two: running, stopped, and *we could not tell*. Folding the third into
 * either of the others is how a strip like this becomes decoration.
 */

export interface HealthAnswer {
  readonly pipeline: { readonly running: boolean; readonly queued: number } | null;
  readonly account:
    | { readonly ok: true; readonly account: string; readonly smsPrice: string }
    | { readonly ok: false }
    | null;
}

export type PipelineState = 'running' | 'stopped' | 'unknown';

export interface HealthView {
  readonly pipeline: PipelineState;
  readonly queued: number;
  /** Som on the account, when it could be read. */
  readonly balance?: number;
  /** What that buys, which is the number somebody can act on. */
  readonly parts?: number;
  readonly balanceLow: boolean;
  /** True when nothing at all could be read — the strip says so rather than lying. */
  readonly unavailable: boolean;
}

export interface HealthOptions {
  /** Below this many affordable segments the balance is called low. */
  readonly minParts: number;
}

export const DEFAULT_MIN_PARTS = 200;

export function healthView(
  answer: HealthAnswer | undefined,
  options: HealthOptions = { minParts: DEFAULT_MIN_PARTS },
): HealthView {
  const pipeline: PipelineState =
    answer?.pipeline == null ? 'unknown' : answer.pipeline.running ? 'running' : 'stopped';

  const account = answer?.account;
  const balance = account != null && account.ok ? finite(account.account) : undefined;
  const price = account != null && account.ok ? finite(account.smsPrice) : undefined;

  // A price of zero is a division that cannot be done, not an infinite balance.
  const parts = balance === undefined || price === undefined || !(price > 0)
    ? undefined
    : Math.floor(balance / price);

  return {
    pipeline,
    queued: answer?.pipeline?.queued ?? 0,
    ...(balance === undefined ? {} : { balance }),
    ...(parts === undefined ? {} : { parts }),
    // Unknown is not low. A balance nobody could read must not raise the alarm
    // that empties a room.
    balanceLow: parts !== undefined && parts < options.minParts,
    unavailable: pipeline === 'unknown' && balance === undefined,
  };
}

/** The provider answers with strings, and `Number('')` is 0 — which is a balance. */
function finite(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}
